import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { KeyRound } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card/Card';
import type { ToolSummary } from '../api/TraceabilityResponseType';
import { findTool } from '../utils/toolSummaries';
import { ToolIdPicker } from './pickers/ToolIdPicker';

interface ManualIdInputProps {
  onSubmit: (toolId: string) => void;
  disabled?: boolean;
  defaultValue?: string;
  /** The company's tools; when known, only an existing ID can be validated. */
  summaries?: readonly ToolSummary[] | null;
  /** "Créer cet outil" on an unknown ID. */
  onCreate?: (toolId: string) => void;
}

export function ManualIdInput({
  onSubmit,
  disabled = false,
  defaultValue,
  summaries = null,
  onCreate,
}: ManualIdInputProps) {
  const [manualId, setManualId] = useState<string>(defaultValue ?? '');
  const { t } = useTranslation();
  const trimmed = manualId.trim();
  // Without the list (loading or unavailable) any ID can be tried, as before.
  const canSubmit = trimmed !== '' && (summaries === null || findTool(trimmed, summaries) !== null);

  const handleSubmit = useCallback(() => {
    if (!canSubmit) return;
    onSubmit(trimmed);
  }, [canSubmit, trimmed, onSubmit]);

  useEffect(() => {
    if (defaultValue !== undefined) setManualId(defaultValue);
  }, [defaultValue]);

  return (
    <Card as="section" className="flex flex-col gap-4">
      <div className="space-y-1">
        <h3 className="flex items-center gap-2 text-base font-semibold text-foreground">
          <KeyRound className="h-4 w-4 text-primary" />
          {t('traceability.scanPage.manual.title')}
        </h3>
        <p className="text-sm text-muted-foreground">
          {t('traceability.scanPage.manual.description')}
        </p>
      </div>
      <div className="space-y-2">
        <Label htmlFor="manual-tool-id" className="text-xs font-medium">
          {t('traceability.scanPage.manual.label')}
        </Label>
        <ToolIdPicker
          id="manual-tool-id"
          value={manualId}
          onChange={setManualId}
          summaries={summaries}
          onEnter={handleSubmit}
          onCreate={onCreate}
          placeholder={t('traceability.scanPage.manual.placeholder')}
          disabled={disabled}
        />
      </div>
      <Button className="w-full" onClick={handleSubmit} disabled={!canSubmit || disabled}>
        <KeyRound aria-hidden />
        {t('traceability.scanPage.manual.submit')}
      </Button>
    </Card>
  );
}
